import {
  Copy,
  Download,
  Edit,
  Eye,
  Mail,
  Printer,
  Receipt,
  Trash2,
} from 'lucide-react';
import type { RowActionItem, RowMenuEntry } from '../components/transactions/RowActionsMenu';
import type { Quotation } from '../types/sales/quotation';
import {
  canConvertQuotationToInvoice,
  canManageQuotations,
} from './salesTransactionPermissions';

export interface QuotationRowActionHandlers {
  onOpen: () => void;
  onConvertToInvoice: () => void;
  onPrint: () => void;
  onDownloadPdf: () => void;
  onSend: () => void;
  onCopy: () => void;
  onDelete: () => void;
}

export interface QuotationRowActionsLayout {
  inlineActions: RowActionItem[];
  menuEntries: RowMenuEntry[];
}

export const canDeleteQuotations = () =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const buildActionCatalog = (
  handlers: QuotationRowActionHandlers
): Map<string, RowActionItem> => {
  const catalog = new Map<string, RowActionItem>();
  const canManage = canManageQuotations();

  catalog.set('edit', {
    id: 'edit',
    label: canManage ? 'Edit' : 'View',
    icon: canManage ? Edit : Eye,
    onClick: handlers.onOpen,
  });

  if (canConvertQuotationToInvoice()) {
    catalog.set('convert_to_invoice', {
      id: 'convert_to_invoice',
      label: 'Convert',
      icon: Receipt,
      variant: 'primary',
      showLabel: true,
      onClick: handlers.onConvertToInvoice,
    });
  }

  catalog.set('print', {
    id: 'print',
    label: 'Print',
    icon: Printer,
    onClick: handlers.onPrint,
  });

  catalog.set('download_pdf', {
    id: 'download_pdf',
    label: 'Download',
    icon: Download,
    onClick: handlers.onDownloadPdf,
  });

  if (canManage) {
    catalog.set('send', {
      id: 'send',
      label: 'Send email',
      icon: Mail,
      onClick: handlers.onSend,
    });

    catalog.set('copy', {
      id: 'copy',
      label: 'Copy',
      icon: Copy,
      onClick: handlers.onCopy,
    });
  }

  if (canDeleteQuotations()) {
    catalog.set('delete', {
      id: 'delete',
      label: 'Delete',
      icon: Trash2,
      variant: 'destructive',
      onClick: handlers.onDelete,
    });
  }

  return catalog;
};

const getInlineActionIds = (catalog: Map<string, RowActionItem>): string[] => {
  const inline: string[] = [];

  if (catalog.has('convert_to_invoice')) inline.push('convert_to_invoice');
  if (catalog.has('edit') && inline.length < 2) inline.push('edit');

  return inline;
};

const MENU_GROUPS: string[][] = [
  ['edit', 'convert_to_invoice'],
  ['print', 'download_pdf', 'send', 'copy'],
  ['delete'],
];

/** Primary actions inline; remaining actions grouped in overflow menu. */
export const buildQuotationRowActions = (
  _quotation: Quotation,
  handlers: QuotationRowActionHandlers
): QuotationRowActionsLayout => {
  const catalog = buildActionCatalog(handlers);
  const inlineIds = new Set(getInlineActionIds(catalog));

  const inlineActions = getInlineActionIds(catalog)
    .map((id) => catalog.get(id))
    .filter((item): item is RowActionItem => item != null);

  const menuEntries: RowMenuEntry[] = [];

  for (const group of MENU_GROUPS) {
    const groupItems = group
      .filter((id) => !inlineIds.has(id) && catalog.has(id))
      .map((id) => catalog.get(id)!);

    if (groupItems.length === 0) continue;
    if (menuEntries.length > 0) menuEntries.push({ type: 'separator' });
    menuEntries.push(...groupItems);
  }

  return { inlineActions, menuEntries };
};
